import {
  Component,
  OnInit,
  TemplateRef,
  ChangeDetectionStrategy,
  input,
  model,
  signal,
  computed,
  effect,
  contentChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { AgxPaginatorComponent } from '../agx-paginator/agx-paginator.component';
import { TableModule } from 'primeng/table';
import { Select } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ShimmerLoaderComponent } from '@app/shared/shimmer-loader/shimmer-loader.component';

export interface ColumnMapping {
  property: string;
  heading: string;
  sticky?: boolean;
}

export interface AggregatedColumn {
  property: string;
  aggregator: (iterable: any[]) => any;
  heading: string;
  hidden?: boolean;
}

export interface StickyRow {
  properties: string[];
  values?: any[];
  aggregator: (iterable: any[], property?: string) => string | number;
  heading: string;
}

interface Column {
  property: string;
  value: string | number;
  rowspan: number;
}

const isObject = (object: object) => {
  return typeof object === 'object' && !Array.isArray(object);
};

@Component({
  selector: 'agx-pivot-table',
  templateUrl: './agx-pivot-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    NgTemplateOutlet,
    AgxPaginatorComponent,
    TableModule,
    Select,
    InputGroup,
    InputGroupAddon,
    ButtonModule,
    AngularSvgIconModule,
    ShimmerLoaderComponent,
  ],
})
export class AgxPivotTableComponent implements OnInit {
  constructor() {
    effect(() => {
      this.data();
      this.selectedColumns();
      if (!this._ready) {
        return;
      }
      this._initialiseTable();
    });
  }

  readonly template = contentChild<TemplateRef<any>>('customContent');

  readonly data = input<any[]>([]);

  readonly columnMappings = input<ColumnMapping[]>(undefined);

  readonly aggregatedColumns = input<AggregatedColumn[]>([]);

  readonly stickyRows = input<StickyRow[]>([]);

  readonly itemsPerPage = input(20);

  readonly isLoading = input(false);

  readonly enableColumnSelection = input(true);

  readonly enableCustomContent = input(false);

  readonly staticColumns = input(false);

  // When set (e.g. '300px'), the table body scrolls at this height with a sticky
  // header instead of growing the page. Passed straight to p-table's scrollHeight.
  readonly scrollHeight = input<string>();

  readonly selectedColumns = model<ColumnMapping[]>([]);

  readonly columnSelectionEnabled = computed(
    () => !this.staticColumns() && this.enableColumnSelection(),
  );

  private readonly _itemsPerPageOverride = signal<number | null>(null);

  /** The effective page size: the paginator's picked override, falling back to the itemsPerPage input. */
  readonly currentItemsPerPage = computed(
    () => this._itemsPerPageOverride() ?? this.itemsPerPage(),
  );

  private _ready = false;

  currentPage = 1;

  rows: Column[][] = [];

  groupedData: any = [];

  get totalItems() {
    return this.rows.length;
  }

  get pageLowerBound() {
    const itemsPerPage = this.currentItemsPerPage();
    return this.currentPage * itemsPerPage - itemsPerPage;
  }

  get pageUpperBound() {
    return this.currentPage * this.currentItemsPerPage();
  }

  private _pageRows: Column[][] = [];

  // we memoize the page rows so that the reference stays stable between recomputes
  // this ensures that the table rows are not recreated on every change-detection pass
  // ensuring that custom content button actions are not lost mid-click
  get pageRows(): Column[][] {
    return this._pageRows;
  }

  private _refreshPageRows(): void {
    const pageRows = this.rows.slice(this.pageLowerBound, this.pageUpperBound);
    this._pageRows = this.withRowSpans(this.selectedColumns(), pageRows);
  }

  ngOnInit() {
    this.aggregatedColumns().push({
      property: 'id',
      aggregator: (iterable: number[]) => iterable,
      heading: 'ID',
      hidden: true,
    });
    this.rows = [];
    this.rows.push(
      this.aggregatedColumns().map((ac) => {
        const data = this.data().map((obj) => obj[ac.property]);
        return {
          property: ac.property,
          value: ac.aggregator(data),
          rowspan: 1,
          hidden: ac.hidden,
        };
      }),
    );
    this._initialiseTable();
    this._ready = true;
  }

  // rebuilds the row view model and the sticky-row totals together, so the two
  // never drift out of sync (e.g. totals frozen against stale/empty data while
  // rows correctly re-render on a later `data()` change).
  private _initialiseTable(): void {
    this.buildViewModel();
    this.buildStickyRows();
  }

  newColumnProperty: string | null = null;

  onAddColumn(property: string): void {
    if (property) {
      this.addSelectedColumn(property);
    }
    setTimeout(() => {
      this.newColumnProperty = null;
    });
  }

  /**
   * Fired when the new column dropdown change event is fired,
   * adds to selected columns and rebuilds table rows
   * @param property The property to add to the selected columns
   */
  addSelectedColumn(property: string) {
    let mapping;
    if (property === 'id') {
      const index = this.aggregatedColumns().indexOf(
        this.aggregatedColumns().find(
          (ac: AggregatedColumn) => ac.property === 'id',
        ),
      );
      if (index !== -1) {
        this.aggregatedColumns().splice(index, 1);
      }
      mapping = {
        property: 'id',
        heading: 'ID',
        sticky: false,
      };
    } else {
      mapping = this.columnMappings().find((m) => m.property === property);
    }
    if (!this.isColumnSelected(mapping)) {
      this.selectedColumns().push(mapping);
      this.buildViewModel();
    }
  }

  /**
   * Removes a selected column and rebuilds the table rows
   * @param mapping The ColumnMapping to remove from the selected rows
   */
  removeSelectedColumn(mapping: ColumnMapping) {
    if (mapping.property === 'id') {
      this.aggregatedColumns().push({
        property: 'id',
        aggregator: (iterable: number[]) => iterable,
        heading: 'ID',
        hidden: true,
      });
    }
    const selectedColumns = this.selectedColumns();
    selectedColumns.splice(selectedColumns.indexOf(mapping), 1);
    this.buildViewModel();
  }

  /**
   * Change an existing selected column to a new column
   */
  onSelectedColumnChange(index: number, property: string) {
    const mapping = this.columnMappings().find((m) => m.property === property);
    if (!this.isColumnSelected(mapping)) {
      this.selectedColumns()[index] = mapping;
      this.buildViewModel();
    }
  }

  /**
   * Move a specified column one index to the right
   * @param index The index of the column to shift
   */
  moveColumnRight(index: number) {
    const selectedColumns = this.selectedColumns();
    const temp = selectedColumns[index];
    selectedColumns[index] = selectedColumns[index + 1];
    selectedColumns[index + 1] = temp;
    this.buildViewModel();
  }

  /**
   * Move a specified column one index to the left
   * @param index The index of the column to shift
   */
  moveColumnLeft(index: number) {
    const selectedColumns = this.selectedColumns();
    const temp = selectedColumns[index];
    selectedColumns[index] = selectedColumns[index - 1];
    selectedColumns[index - 1] = temp;
    this.buildViewModel();
  }

  isColumnSelected(columnMapping: ColumnMapping): boolean {
    const selectedColumns = this.selectedColumns();
    if (!selectedColumns || !columnMapping) {
      return false;
    }
    return (
      selectedColumns.findIndex(
        (selected) => selected.property === columnMapping.property,
      ) > -1
    );
  }

  /** Column mappings not yet selected - the options for the "Add column" picker. */
  get unselectedColumns(): ColumnMapping[] {
    return (this.columnMappings() ?? []).filter(
      (m) => !this.isColumnSelected(m),
    );
  }

  /** Options for a selected column's picker: the current column plus any unselected ones. */
  availableColumns(current: ColumnMapping): ColumnMapping[] {
    return [current, ...this.unselectedColumns];
  }

  /** Total rendered columns, used for the empty-message colspan. */
  get visibleColumnCount(): number {
    const aggregated = this.aggregatedColumns().filter(
      (ac) => !ac.hidden,
    ).length;
    const custom = this.enableCustomContent() ? 1 : 0;
    return this.selectedColumns().length + aggregated + custom;
  }

  onPageChange(pageNumber: number) {
    this.currentPage = pageNumber;
    this._refreshPageRows();
  }

  /** Change the page size (from the paginator's picker) and jump back to page 1. */
  changePageSize(itemsPerPage: number): void {
    this._itemsPerPageOverride.set(itemsPerPage);
    this.currentPage = 1;
    this._refreshPageRows();
  }

  /**
   * Clear the column selections
   */
  clearSelections() {
    this.selectedColumns.set([]);
    this.buildViewModel();
  }

  /**
   * Build the table rows
   */
  buildViewModel() {
    const selectedColumns = this.selectedColumns();
    if (selectedColumns && selectedColumns.length > 0) {
      this.groupedData = this.nest(
        selectedColumns.map((c) => c.property),
        this.data(),
      );
      this.rows = [];
      this.traverseGroupedData(this.groupedData);
    } else {
      this.groupedData = [];
      this.rows = [];
      this.rows.push(
        this.aggregatedColumns().map((ac) => {
          const data = this.data().map((obj) => obj[ac.property]);
          return {
            property: ac.property,
            value: ac.aggregator(data),
            rowspan: 1,
            hidden: ac.hidden,
          };
        }),
      );
    }

    this._clampCurrentPage();
    this._refreshPageRows();
  }

  private _clampCurrentPage(): void {
    const lastPage = Math.max(
      1,
      Math.ceil(this.rows.length / this.currentItemsPerPage()),
    );

    if (this.currentPage > lastPage) {
      this.currentPage = lastPage;
    }
  }

  /**
   * Recursively grouped data into a nested object based on property keys
   * @param keys The keys to group the data on
   * @param array The data to group
   */
  private nest(keys: string[], array: object[]) {
    if (keys.length === 0) {
      return array;
    }

    const first = keys[0];
    const rest = keys.slice(1);

    return this.mapValue(this.groupBy(first, array), (value: object[]) => {
      return this.nest(rest, value);
    });
  }

  /**
   * Get a copy of an object after a mutator has been applied to each property
   * @param object The object to copy
   * @param iteratee The property mutator
   */
  private mapValue(object: object, iteratee: any) {
    object = Object(object);
    const result = {};
    Object.keys(object).forEach((key) => {
      result[key] = iteratee(object[key]);
    });
    return result;
  }

  /**
   * Groups an array of objects based on a object key
   * @param key The object key to group the data on
   * @param array The array to group
   */
  private groupBy(key: string, array: object[]) {
    const objectsByKeyValue = {};
    // simple for loop for best performance
    for (let i = 0, l = array.length; i < l; i++) {
      let value = array[i][key];
      if (!value && key.includes('_accessObject_')) {
        // dig down into specified object(s)
        value = key
          .split('_accessObject_')
          .reduce((j, k) => j && j[k], array[i]);
      }
      if (typeof objectsByKeyValue[value] === 'undefined') {
        objectsByKeyValue[value] = [];
      }
      objectsByKeyValue[value].push(array[i]);
    }
    return objectsByKeyValue;
  }

  /**
   * Recursively traverse the grouped data and build table rows
   * @param data The grouped data object
   */
  private traverseGroupedData(data: object) {
    if (isObject(data)) {
      const keys = Object.keys(data);
      keys.forEach((key) => {
        if (isObject(data[key])) {
          this.traverseGroupedData(data[key]);
        } else {
          this.rows.push(
            this.selectedColumns()
              .map((column) => {
                let value = data[key][0][column.property];
                if (!value && column.property.includes('_accessObject_')) {
                  // If no value and property contains specific string indicating we want to dig down
                  // into a nested object, split on the string and traverse the object.
                  // Arbitrary levels of nesting is supported
                  value = column.property
                    .split('_accessObject_')
                    .reduce((j, k) => j && j[k], data[key][0]);
                }
                return {
                  property: column.property,
                  value,
                  rowspan: 1,
                };
              })
              .concat(
                this.aggregatedColumns().map((ac) => {
                  const groupedData = data[key].map(
                    (obj: object) => obj[ac.property],
                  );
                  return {
                    property: ac.property,
                    value: ac.aggregator(groupedData),
                    rowspan: 1,
                    hidden: ac.hidden,
                  };
                }),
              ),
          );
        }
      });
    }
  }

  /**
   * Recursively set the column rowspans of a table based upon the occurrence of value in the data subset
   */
  /**
   * Returns a copy of `rows` with per-column rowspans applied for grouped
   * display: the first row of a group keeps its group cell (carrying the
   * rowspan) while the following rows in that group omit it. Pure - it reads
   * the input rows and returns fresh cells, never mutating the source.
   */
  private withRowSpans(
    selectedColumns: ColumnMapping[],
    rows: Column[][],
  ): Column[][] {
    const groupProperties = (selectedColumns ?? []).map((c) => c.property);

    return rows.map((row, index) =>
      row
        .filter((cell) => {
          const level = groupProperties.indexOf(cell.property);
          // Non-group cells always render; a group cell renders only on the
          // first row of its group.
          return (
            level === -1 ||
            this.startsGroup(rows, index, groupProperties, level)
          );
        })
        .map((cell) => {
          const level = groupProperties.indexOf(cell.property);
          if (level === -1) {
            return { ...cell };
          }
          return {
            ...cell,
            rowspan: this.groupSpan(rows, index, groupProperties, level),
          };
        }),
    );
  }

  /** True when the row at `index` opens a new group at `level` (differs from the row above). */
  private startsGroup(
    rows: Column[][],
    index: number,
    properties: string[],
    level: number,
  ): boolean {
    return (
      index === 0 ||
      !this.sameGroup(rows[index - 1], rows[index], properties, level)
    );
  }

  /** Count of consecutive rows from `index` sharing the grouping values up to `level`. */
  private groupSpan(
    rows: Column[][],
    index: number,
    properties: string[],
    level: number,
  ): number {
    let span = 1;
    for (let next = index + 1; next < rows.length; next++) {
      if (!this.sameGroup(rows[index], rows[next], properties, level)) {
        break;
      }
      span++;
    }
    return span;
  }

  private sameGroup(
    a: Column[],
    b: Column[],
    properties: string[],
    level: number,
  ): boolean {
    for (let l = 0; l <= level; l++) {
      if (
        this.cellValue(a, properties[l]) !== this.cellValue(b, properties[l])
      ) {
        return false;
      }
    }
    return true;
  }

  private cellValue(
    row: Column[],
    property: string,
  ): string | number | undefined {
    return row.find((cell) => cell.property === property)?.value;
  }

  buildStickyRows() {
    const stickyRows = this.stickyRows();
    if (stickyRows) {
      // Reset sticky row values
      stickyRows.forEach((stickyRow) => {
        stickyRow.values = Array(
          this.aggregatedColumns().filter((ac) => !ac.hidden).length,
        ).fill('-');
      });

      // Loop though aggregated columns first so that we calculate
      // sticky row values in order of ACs.
      this.aggregatedColumns().forEach((ac, index) => {
        this.stickyRows().forEach((stickyRow) => {
          stickyRow.properties.forEach((property) => {
            if (property === ac.property) {
              const data = this.data().map((obj) => obj[ac.property]);
              // replace '-' with calculated value
              stickyRow.values.splice(
                index,
                1,
                stickyRow.aggregator(data, ac.property),
              );
            }
          });
        });
      });
    }
  }

  isAggregatedColumn(column: Column): boolean {
    return this.aggregatedColumns().some(
      (ac: AggregatedColumn) => ac?.property === column?.property,
    );
  }
}
